import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingCart, Lock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/api';
import { buildContactHref } from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const CartLine = ({ item, setQty, remove }) => (
    <div className="flex items-center gap-4 py-5" data-testid={`cart-line-${item.id}`}>
        <Link to={`/machines/${item.slug || item.id}`} className="w-20 h-20 border border-neutral-200 bg-neutral-50 flex-shrink-0 overflow-hidden">
            {item.image_url ? <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" /> : null}
        </Link>
        <div className="flex-1 min-w-0">
            <Link to={`/machines/${item.slug || item.id}`} className="font-display font-bold uppercase text-neutral-900 hover:text-brand-primary">{item.name}</Link>
            <div className="text-sm text-neutral-500">{formatPrice(item.price)} each</div>
        </div>
        <div className="flex items-center border border-neutral-300">
            <button onClick={() => setQty(item.id, item.qty - 1)} className="p-2 hover:bg-neutral-100" data-testid={`cart-dec-${item.id}`}><Minus className="h-4 w-4" /></button>
            <span className="w-10 text-center font-mono" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
            <button onClick={() => setQty(item.id, item.qty + 1)} className="p-2 hover:bg-neutral-100" data-testid={`cart-inc-${item.id}`}><Plus className="h-4 w-4" /></button>
        </div>
        <div className="w-28 text-right font-display font-black text-neutral-900" data-testid={`cart-line-total-${item.id}`}>{formatPrice(item.qty * item.price)}</div>
        <button onClick={() => remove(item.id)} className="p-2 text-neutral-400 hover:text-red-600" data-testid={`cart-remove-${item.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
);

const Cart = () => {
    const { items, setQty, remove, subtotal } = useCart();
    useSEO({ title: 'Your Cart — Dry Ice Bulls Equipment', description: 'Review your dry ice cleaning equipment and accessories order.' });

    const quoteMsg = items.map((i) => `${i.qty} x ${i.name}`).join(', ');

    return (
        <div className="container-page pt-32 pb-24" data-testid="cart-page">
            <div className="label-caps text-brand-primary mb-4">/ Your order</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl text-neutral-900">Cart</h1>

            {items.length === 0 ? (
                <div className="mt-12 border border-neutral-200 p-10 text-center" data-testid="cart-empty">
                    <ShoppingCart className="h-10 w-10 mx-auto text-neutral-300" />
                    <p className="mt-4 text-neutral-600">Your cart is empty.</p>
                    <Link to="/shop" className="mt-6 inline-block bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 py-3 label-caps transition-colors" data-testid="cart-continue-shopping">Shop equipment</Link>
                </div>
            ) : (
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8 border-t border-neutral-200 divide-y divide-neutral-200">
                        {items.map((i) => <CartLine key={i.id} item={i} setQty={setQty} remove={remove} />)}
                    </div>
                    <aside className="lg:col-span-4 border border-neutral-200 bg-neutral-50 p-6 h-fit" data-testid="cart-summary">
                        <div className="flex justify-between text-neutral-700"><span>Subtotal</span><span className="font-bold" data-testid="cart-subtotal">{formatPrice(subtotal)}</span></div>
                        <div className="flex justify-between text-sm text-neutral-500 mt-2"><span>Shipping & tax</span><span>Calculated at checkout</span></div>
                        <Link to="/checkout" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 py-4 label-caps transition-colors" data-testid="cart-checkout-btn">
                            <Lock className="h-4 w-4" /> Secure checkout
                        </Link>
                        <Link
                            to={buildContactHref({ subject: `Order request: ${quoteMsg}`, interest: 'quote' })}
                            className="mt-3 w-full inline-flex items-center justify-center border border-neutral-900 hover:bg-neutral-900 hover:text-white text-neutral-900 font-bold px-6 py-3 label-caps transition-colors"
                            data-testid="cart-request-invoice"
                        >
                            Request an invoice instead
                        </Link>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default Cart;
